import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, formatErr } from "@/lib/auth";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import GoogleButton from "@/components/GoogleButton";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import { CreditCard, Mail, RefreshCw } from "lucide-react";

export default function Register() {
  const { t } = useTranslation();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingVerify, setPendingVerify] = useState(null);
  const [resending, setResending] = useState(false);
  const { register, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect");
  // A "Book with X" button on /team can drop us here with `?therapist=<id>`
  // — pass that straight through so the patient lands on the picked
  // clinician's dashboard on day one.
  const preferredTherapistId = params.get("therapist_id") || params.get("therapist");
  // Clinician preference is now chosen HERE at registration (not later in
  // onboarding). Reuse the same public team source the onboarding card used.
  const [team, setTeam] = useState([]);
  const [therapistId, setTherapistId] = useState(preferredTherapistId || "");
  const [therapistName, setTherapistName] = useState("");
  useEffect(() => {
    api.get("/public/clinicians").then((r) => setTeam(r.data || [])).catch(() => {});
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // Detect "the user landed here from a paid CTA" (Book/Subscribe on /prices).
  // We surface a contextual banner so the reason for the account creation is
  // obvious ("You're about to pay — create an account first"). Any redirect
  // to a checkout URL or the in-app booking flow qualifies.
  const paymentContext = useMemo(() => {
    if (!redirect) return null;
    const r = decodeURIComponent(redirect);
    if (r.startsWith("/checkout/summary") || r.includes("/checkout/summary")) return "checkout";
    if (r.startsWith("/app/appointments") || r.includes("/app/appointments")) return "booking";
    return null;
  }, [redirect]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      // Public register is patient-only. Server ignores role/hcpc/profile
      // fields — we only send the three that matter (plus an optional
      // preferred-therapist id when the user came from "Book with X").
      const payload = { name: f.name, email: f.email, password: f.password };
      // Selected clinician (or the ?therapist= preselection). The server
      // validates it maps to an ACTIVE clinician and otherwise falls back to
      // the least-loaded clinician — never a random one.
      if (therapistId) {
        payload.preferred_therapist_id = therapistId;
        const picked = team.find((tm) => tm.id === therapistId);
        if (picked?.name || therapistName) payload.preferred_therapist_name = picked?.name || therapistName;
      }
      const u = await register(payload);

      // Book-flow shortcut: when the user came from Book/Subscribe, keep
      // them in the checkout funnel — the register call issued a JWT so
      // Stripe checkout works even before email verification. The
      // confirmation email is fired server-side; we simply toast it and go.
      //
      // For the Book flow specifically, first route through the one-screen
      // "Before your first visit" welcome so the patient knows what to
      // expect and what to bring. It's shown once (localStorage-gated) and
      // then continues to the picked booking URL.
      if (paymentContext && redirect && redirect.startsWith("/")) {
        toast.success(`Verification email sent to ${u.email || f.email} — you can finish payment now`);
        if (paymentContext === "booking") {
          const seen = (() => { try { return !!localStorage.getItem("fl_seen_before_first_visit_v1"); } catch { return false; } })();
          if (seen) { nav(redirect); return; }
          // Extract picked service name (if any) so the welcome can adapt
          // its tone — performance / post-op / standard assessment.
          let svc = "";
          try {
            const u2 = new URL(redirect, window.location.origin);
            svc = u2.searchParams.get("service") || "";
          } catch {}
          const q = new URLSearchParams({ redirect, service: svc }).toString();
          nav(`/before-first-visit?${q}`);
          return;
        }
        nav(redirect);
        return;
      }

      // Everyone else must confirm their email before they can use the app.
      // Drop the token we just got and show the "check your inbox" screen.
      if (u.role === "patient" && u.email_verified === false) {
        await logout();
        setPendingVerify({ email: u.email || f.email });
        return;
      }
      if (redirect && redirect.startsWith("/")) {
        nav(redirect);
      } else {
        // Fresh patients land on the onboarding step to capture sport/goal/therapist.
        nav("/onboarding");
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const resendVerification = async () => {
    if (!pendingVerify?.email) return;
    setResending(true);
    try {
      await api.post("/security/email/resend-public", { email: pendingVerify.email });
      toast.success("Verification email sent — check your inbox");
    } catch { toast.error("Could not send — try again shortly"); }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen fl-noise fl-page flex items-center justify-center p-6" style={{ background: "var(--fl-bg)" }}>
      <div className="absolute top-6 right-6 flex items-center gap-2"><LanguageSwitcher /><ThemeToggle /></div>
      <div className="w-full max-w-lg">
        <Link to="/" className="mb-10 flex justify-center">
          <Logo variant="wordmark" size="lg" />
        </Link>
        <div className="fl-card p-8">
          <h1 className="fl-heading text-3xl">{t("auth.createAccount")}</h1>
          <p className="text-zinc-400 mt-2 text-sm">{t("auth.createSub")}</p>

          {paymentContext && !pendingVerify && (
            <div
              className="mt-6 rounded-md border border-[#f47a20]/40 bg-[#f47a20]/10 p-4 flex items-start gap-3"
              data-testid="reg-payment-banner"
            >
              <CreditCard size={18} className="text-[#f47a20] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-zinc-100">
                  {paymentContext === "checkout"
                    ? "Create your account before paying"
                    : "Create your account to book"}
                </div>
                <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
                  {paymentContext === "checkout"
                    ? "You're one step away from securing your programme. We just need a quick account so your purchase, sessions and clinician notes stay under your name — you'll come straight back to the payment page."
                    : "You're one step away from booking. Create a quick account so we can hold your appointment against your name and give you access to your rehab plan."}
                </div>
              </div>
            </div>
          )}

          {pendingVerify ? (
            <div className="mt-8 space-y-4" data-testid="reg-verify-gate">
              <div className="w-14 h-14 rounded-full bg-[#f47a20]/15 border border-[#f47a20]/40 flex items-center justify-center mx-auto">
                <Mail size={24} className="text-[#f47a20]" />
              </div>
              <div className="text-center">
                <div className="fl-heading text-2xl">Account created — one more step</div>
                <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                  We sent a verification link to <span className="text-zinc-100 font-medium">{pendingVerify.email}</span>.
                  Click it to activate your account, then come back and sign in
                  {paymentContext === "checkout" ? " — we'll take you straight back to the payment page." : "."}
                </p>
                <p className="text-[11px] text-zinc-500 mt-2">Can't find it? Check your spam folder — or hit resend below.</p>
              </div>
              <button
                onClick={resendVerification}
                disabled={resending}
                className="fl-btn w-full flex items-center justify-center gap-2"
                data-testid="reg-verify-resend"
              >
                <RefreshCw size={13} className={resending ? "animate-spin" : ""} />
                {resending ? "Sending…" : "Resend verification email"}
              </button>
              <Link
                to="/login"
                className="fl-btn-ghost w-full text-sm text-center block"
                data-testid="reg-verify-signin"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
          <>
          <div className="mt-6"><GoogleButton testId="reg-google" label={t("auth.signUpGoogle")} /></div>
          <div className="my-6 flex items-center gap-3 text-xs text-zinc-600 uppercase tracking-wider">
            <div className="flex-1 h-px bg-white/10" /> {t("auth.orEmail")} <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <div className="fl-label mb-2">{t("auth.fullName")}</div>
              <input className="fl-input" required value={f.name} onChange={set("name")} data-testid="reg-name" />
            </div>
            <div>
              <div className="fl-label mb-2">{t("auth.email")}</div>
              <input className="fl-input" type="email" required value={f.email} onChange={set("email")} data-testid="reg-email" />
            </div>
            <div>
              <div className="fl-label mb-2">{t("auth.password")}</div>
              <input className="fl-input" type="password" required minLength={6} value={f.password} onChange={set("password")} data-testid="reg-password" />
            </div>
            {team.length > 0 && (
              <div>
                <div className="fl-label mb-2">Preferred clinician <span className="text-zinc-500 normal-case font-normal">· optional</span></div>
                <select
                  className="fl-input"
                  value={therapistId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setTherapistId(id);
                    setTherapistName((team.find((tm) => tm.id === id) || {}).name || "");
                  }}
                  data-testid="reg-clinician"
                >
                  <option value="">No preference — assign the best fit</option>
                  {team.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}{m.role ? ` — ${m.role}` : ""}</option>
                  ))}
                </select>
                <div className="text-[11px] text-zinc-500 mt-1">Choose your clinician or leave as “No preference” and we’ll match you to the best-fit clinician.</div>
              </div>
            )}
            <div className="text-[11px] text-zinc-500 -mt-2">
              Patient sign-up only. Clinician and staff accounts are created by invitation — please contact us if you need one.
            </div>
            {err && <div className="text-sm text-red-400" data-testid="reg-error">{err}</div>}
            <button className="fl-btn w-full justify-center" disabled={loading} data-testid="reg-submit">{loading ? t("common.loading") : t("auth.signUpBtn")}</button>
          </form>
          </>
          )}
          <div className="mt-6 text-sm text-zinc-400 text-center">
            {t("auth.haveAccount")} <Link to={redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : "/login"} className="text-[#f47a20]">{t("auth.signInBtn")}</Link>
            <div className="mt-2"><Link to="/trust" className="text-zinc-500 hover:text-[#f47a20] text-xs" data-testid="register-trust-link">Trust & Data Safety</Link></div>
          </div>
        </div>
      </div>
    </div>
  );
}
